import React, {createContext, useContext, useEffect, useMemo, useState} from 'react';
import {Spinner} from '@chakra-ui/react'
import Cookies from 'js-cookie';

import {useApi} from '@/uses/useApi';
import Login from '@/routes/auth/Login';
import objectUtil from '@/utils/objectUtil';

const PUBLIC_ROUTES = ['/login', '/reset-password'];

const UserContext = createContext();

export const useUser = () => useContext(UserContext);
export const getStoredUser = () => {
    try {
        let userValue = '';
        let index = 0;
        while (Cookies.get(`user-part${index}`)) {
            userValue += Cookies.get(`user-part${index}`);
            index++;
        }
        if (!userValue) {
            userValue = Cookies.get('user');
        }
        return JSON.parse(userValue);
    } catch (err) {
        return null;
    }
};

export const UserProvider = ({children}) => {
    const [user, setUser] = useState(undefined);
    //-1 not init, 0 init but loading, 1 ready
    const [state, setState] = useState(-1);
    const api = useApi();

    const handleAuthStorageChanged = (currentAuthStorage) => {
        try {
            //check user integrity
            if (!['user'].every(key => currentAuthStorage && currentAuthStorage?.hasOwnProperty(key))) {
                currentAuthStorage = null;
            }

            setUser(prevUser => {
                if (!objectUtil.deepEqual((prevUser ?? {}), (currentAuthStorage ?? {}))) {
                    return currentAuthStorage;
                }
                /** Avoid keeping the screen loading when user is undefined (first state) */
                return prevUser ?? null;
            });

        } catch (err) {
            console.error('ERROR_PARSING_CURRENTAUTHSTORAGE', err);
            setUser(null);
        }
    }

    useEffect(() => {
        const fetchData = async () => {
            const currentAuthStorage = getStoredUser();
            if (!currentAuthStorage?.user) {
                await api.AuthService.isLogged();
            } else {
                handleAuthStorageChanged(currentAuthStorage);
            }
        };

        document.addEventListener('authStorageChanged', (e) => handleAuthStorageChanged(e?.detail));

        fetchData();

        return () => {
            document.removeEventListener('authStorageChanged', (e) => handleAuthStorageChanged(e?.detail));
        };
    }, []);

    useEffect(() => {
        /** Avoids triggering this effect on element mount */
        if (user !== undefined) {
            setState(1);
        }
    }, [user]);

    const isAdmin = useMemo(() => user?.role === 'ADMIN', [user]);
    const role = useMemo(() => user?.role, [user]);
    const currentFeatures = useMemo(() => user?.features ?? [], [user]);

    const login = async (data) => {
        if (state !== 1) {
            return;
        }
        setState((prevState) => prevState === -1 ? prevState : 0);
        try {
            const response = await api.AuthService.login(data);
            setUser(response?.data?.value ?? getStoredUser() ?? null);
            return response;
        } catch (err) {
            setUser(getStoredUser() ?? null);
            throw err;
        } finally {
            setState((prevState) => prevState === -1 ? prevState : 1);
        }
    };

    const logout = async () => {
        if (state !== 1) {
            return;
        }
        setState((prevState) => prevState === -1 ? prevState : 0);
        return api.AuthService.logout().then(response => {
            return response;
        }).catch(err => {
            throw err;
        })
    };

    const register = async (data) => {
        if (state !== 1) {
            return;
        }
        setState((prevState) => prevState === -1 ? prevState : 0);
        return api.AuthService.register(data).then(response => {
            return response;
        }).catch(err => {
            throw err;
        })
    };

    const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
    const publicPath = PUBLIC_ROUTES.some((route) => currentPath === route || currentPath.startsWith(`${route}/`));

    const child = useMemo(() => {
        if (state === -1) {
            return <Spinner alignSelf="center"/>
        }
        if (state !== -1 && !user && !publicPath) {
            return <Login/>
        }
        return children
    }, [state, user, publicPath, children]);

    return (
        <UserContext.Provider value={{
            user,
            role,
            isAdmin,
            state,
            login,
            logout,
            register,
            currentFeatures,
        }}>
            {child}
        </UserContext.Provider>
    );
};

export default UserProvider
